import { useSelector } from "react-redux";
import { useAppConfig } from "@/appConfig";
import { getLocalStorage } from "@/helpers/common";
import { USER_INFO_KEY } from "@/constants/common";
import { wantAdBudgetValue } from "@/helpers/createWantAd";
import propertyPhoto from "@/images/house-empty-placeholder-02.png";
import { useTourState } from "./store";
import { useTourSamples } from "./samples";

export const TOUR_SAMPLE_SLUG = "tour-sample";
export const TOUR_NETWORK_SLUG = "tour-network";
const SAMPLE_AGENT_ID = -1;
// Sample rows never exist on the server. Ids 0 and below are the sentinels, and
// some callers (view tracking, plan/introduction queries) skip a request only
// because 0 is falsy, so anything that can fire a real request for a negative
// id (the demand property's report download) must check isTourRecord instead.
const SAMPLE_RECORD_ID = 0;
const SAMPLE_DEMAND_PROPERTY_ID = -1;

export const isTourRecord = (record) => record?.isTourSample === true;

const initialsOf = (agent) => `${agent.firstName?.[0] || ""}${agent.lastName?.[0] || ""}`;

export const sampleAgentOf = (tenant) => {
  const agent = { firstName: "Jordan", lastName: "Ellis", company: "ABC Realty", ...tenant?.tourSample?.agent };
  return {
    ...agent,
    userId: SAMPLE_AGENT_ID,
    avatar:
      agent.avatar ||
      `data:image/svg+xml;utf8,${encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56 56"><rect width="56" height="56" rx="28" fill="#5b6f8f"/><text x="28" y="35" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="20" font-weight="700" fill="#fff">${initialsOf(agent)}</text></svg>`,
      )}`,
  };
};

export const placeOf = (samples, tenant) => {
  const market = tenant?.defaultMarket || {};
  return {
    city: samples.market.city || market.city || "",
    state: samples.market.state || market.state || "",
    lat: market.latitude,
    lng: market.longitude,
  };
};

const where = (...parts) => parts.filter(Boolean).join(", ");

// Polygon text must be a closed ring: first point repeated at the end.
const closedRing = (area) => {
  const [firstLng, firstLat] = area[0];
  const [lastLng, lastLat] = area[area.length - 1];
  return firstLng === lastLng && firstLat === lastLat ? area : [...area, area[0]];
};

const polygonText = (area) =>
  Array.isArray(area) && area.length > 2
    ? `POLYGON((${closedRing(area).map(([lng, lat]) => `${lng} ${lat}`).join(", ")}))`
    : null;

const polygonPoints = (area) =>
  Array.isArray(area) && area.length > 2 ? area.map(([lng, lat]) => ({ lng, lat })) : null;

export const useViewer = () => {
  const details = useSelector((state) => state.userProfile?.userDetails?.[0]) || {};
  const info = getLocalStorage(USER_INFO_KEY)?.userInfo || {};
  return {
    userId: info.userId || null,
    firstName: details.FirstName || info.firstName || "",
    lastName: details.LastName || info.lastName || "",
    company: details.Company || "",
    avatar: details.Img || "",
  };
};

export const wantListingRow = (samples, place, now = new Date()) => ({
  Want_ad_Id: SAMPLE_RECORD_ID,
  slug: TOUR_SAMPLE_SLUG,
  CreatedOn: now.toISOString(),
  Title_Text: samples.wantListing.title,
  Location: where(place.city, place.state),
  IsPublished: 1,
  perfectFits: samples.wantListing.matchingHomes,
  aiPending: false,
  IntroReceived: 1,
  isTourSample: true,
});

export const propertyRow = (samples, place, now = new Date()) => ({
  Inv_Id: SAMPLE_RECORD_ID,
  Want_ad_Id: SAMPLE_RECORD_ID,
  CreatedOn: now.toISOString(),
  Location: `Sample property in ${where(samples.property.neighborhood || place.city, place.state)}`,
  photo: propertyPhoto,
  potentialBuyers: samples.property.demandCount,
  AskingPrice_Sell: samples.property.price,
  Represent_as_Seller: 1,
  IsSavedDraft: 0,
  IsSold: 0,
  isTourSample: true,
});

export const feedRow = (samples, place, agent, now = new Date()) => {
  const sample = samples.wantListing;
  return {
    Want_ad_Id: SAMPLE_RECORD_ID,
    slug: TOUR_NETWORK_SLUG,
    Title_Text: sample.title,
    AdType: "BUY",
    Bedrooms: sample.bedrooms,
    Bathrooms: sample.bathrooms,
    Budget_mn: sample.budgetMin,
    Budget_mx: sample.budgetMax,
    Sqft_mn: sample.sqftMin,
    Sqft_mx: sample.sqftMax,
    N2H: sample.niceToHaves.map((item, index) => ({ N2H_Id: index + 1, Text: item.Text, Icon: item.Icon })),
    subPropertyTypeId: [1],
    Location: where(place.city, place.state, "USA"),
    City: place.city,
    State: place.state,
    DrawnPolygonText: polygonText(sample.area),
    Latitude: place.lat,
    Longitude: place.lng,
    mapRadius: sample.area ? null : sample.radiusMiles,
    CreatedBy: agent.userId,
    CreatedOn: now.toISOString(),
    Is_Agent_Created: 1,
    userFirstName: agent.firstName,
    userLastName: agent.lastName,
    userAvatar: agent.avatar,
    userCompany: agent.company,
    financeTypeText: "Conventional Mortgage",
    isTourSample: true,
  };
};

const describe = (sample, place) => {
  if (sample.description) return sample.description;
  const [first, second] = sample.niceToHaves.map((item) => item.Text.toLowerCase());
  const plus = [first, second].filter(Boolean).join(" and ");
  return [
    `Move-in ready ${sample.bedrooms}-bed, ${sample.bathrooms}-bath single-family property within ${sample.radiusMiles} miles of ${place.city || "town"}, ${wantAdBudgetValue(sample.budgetMin, sample.budgetMax)}.`,
    plus ? `A ${plus} would be a big plus.` : "",
    "Flexible on finishes. Layout, light and a quiet street matter most.",
  ]
    .filter(Boolean)
    .join(" ");
};

export const sampleWantAd = (samples, place, viewer, now = new Date(), slug = TOUR_SAMPLE_SLUG) => {
  const sample = samples.wantListing;
  const area = sample.area
    ? `Custom area in ${where(place.city, place.state)}`
    : `Within ${sample.radiusMiles} mi of ${where(place.city, place.state)}`;
  return {
    wantAdId: SAMPLE_RECORD_ID,
    slug,
    userId: viewer.userId,
    userFirstName: viewer.firstName,
    userLastName: viewer.lastName,
    userAvatar: viewer.avatar,
    userCompany: viewer.company,
    userTypeId: 2,
    isAgentCreated: 1,
    isPublished: 1,
    cmpldPerctg: 100,
    adType: "BUY",
    location: where(place.city, place.state, "USA"),
    prprtyType: 1,
    prptyTypeTxt: "Single-Family",
    nice2Have: sample.niceToHaves.map((item, index) => ({
      id: index + 1,
      order: index + 1,
      tab: item.Tab,
      text: item.Text,
      img: item.Img,
      icon: item.Icon,
    })),
    minBdgt: sample.budgetMin,
    maxBdgt: sample.budgetMax,
    adTitle: sample.title,
    adDesc: describe(sample, place),
    isArchived: 0,
    city: place.city,
    state: place.state,
    zip: null,
    mapRadius: sample.area ? null : sample.radiusMiles,
    drawnPolygon: polygonPoints(sample.area),
    lat: String(place.lat ?? ""),
    lng: String(place.lng ?? ""),
    publishedOn: now.toISOString(),
    createdOn: now.toISOString(),
    buyersAgreementSigned: 1,
    propertyCategory: 1,
    prptySubTypes: [1],
    prptySubTypesTxt: ["Single-Family"],
    timeLine: 3,
    tlText: "1-3 months",
    noBedRm: sample.bedrooms,
    noBathRm: sample.bathrooms,
    sqFtMin: String(sample.sqftMin),
    sqFtMax: String(sample.sqftMax),
    finTypeTxt: "Conventional Mortgage",
    prchTypeTxt: "Primary Residence",
    isStrengthen: 1,
    neighborHood: sample.neighborhood || samples.market.neighborhood || null,
    highSchool: sample.school || null,
    grgSpce: sample.garage || null,
    zipCode: [],
    introCount: 0,
    tourMatchCount: samples.wantListing.matchingHomes,
    matchStatus: "complete",
    isFavourite: false,
    syndicateToThePublic: false,
    shareArea: { kind: sample.area ? "drawn" : "radius", shortLabel: area, longLabel: area, shortPhrase: area.toLowerCase(), longPhrase: area.toLowerCase() },
    isTourSample: true,
  };
};

// ?tourPreview=1 only counts inside the preview iframe, so a pasted link can't
// swap a real page's data for the sample.
const inFrame = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true; // cross-origin parent access throws: that is a frame
  }
};

export const isTourPreviewFrame = () => {
  try {
    return inFrame() && new URLSearchParams(window.location.search).get("tourPreview") === "1";
  } catch {
    return false;
  }
};

const useActiveSeed = (...sections) => {
  const { phase, seed } = useTourState();
  if (isTourPreviewFrame()) return sections.includes("buy");
  return phase !== "idle" && sections.includes(seed);
};

const demandListSize = (demandCount) => Math.max(demandCount, 5);

export const sampleDemandList = (samples) => {
  const { demandCount = 7, demandBudgetMin = 300000, demandBudgetMax = 360000 } = samples.property || {};
  const count = demandListSize(demandCount);
  return Array.from({ length: count }, (_, i) => {
    const mid = demandBudgetMin + ((demandBudgetMax - demandBudgetMin) * i) / Math.max(count - 1, 1);
    const spread = 15000 + (i % 3) * 10000;
    return { Want_ad_Id: -(i + 1), Budget_mn: Math.round(mid - spread), Budget_mx: Math.round(mid + spread) };
  });
};

export const useTourDemand = () => {
  const active = useActiveSeed("sell") && !isTourPreviewFrame();
  const samples = useTourSamples(active);
  const { tenant } = useAppConfig() || {};
  if (!active) return null;
  const list = sampleDemandList(samples);
  const property = {
    ...propertyRow(samples, placeOf(samples, tenant)),
    Inv_Id: SAMPLE_DEMAND_PROPERTY_ID,
    potentialBuyers: list.length,
    reportMatchCount: list.length,
  };
  return { property, list, count: list.length };
};

export const useTourSeed = (section) => {
  const active = useActiveSeed(section);
  const samples = useTourSamples(active);
  const { tenant } = useAppConfig() || {};
  if (!active) return null;
  const place = placeOf(samples, tenant);
  if (section === "feed") return feedRow(samples, place, sampleAgentOf(tenant));
  if (section === "buy") return wantListingRow(samples, place);
  if (section === "sell") return propertyRow(samples, place);
  return null;
};

export const isTourSlug = (slug) => slug === TOUR_SAMPLE_SLUG || slug === TOUR_NETWORK_SLUG;

export const sampleIntroListing = (samples, place) => ({
  mlsListingKey: `tour-${SAMPLE_RECORD_ID}`,
  Location: where("1234 Main Street", place.city, place.state),
  photo: propertyPhoto,
  AskingPrice: samples.property.price,
  selectable: true,
  isTourSample: true,
});

export const useTourSampleWantAd = (slug) => {
  const { phase } = useTourState();
  const touring = phase !== "idle" || isTourPreviewFrame();
  const own = touring && slug === TOUR_SAMPLE_SLUG;
  const network = touring && slug === TOUR_NETWORK_SLUG;
  const samples = useTourSamples(own || network);
  const { tenant } = useAppConfig() || {};
  const viewer = useViewer();
  if (!own && !network) return null;
  const owner = own ? viewer : sampleAgentOf(tenant);
  const place = placeOf(samples, tenant);
  return { ...sampleWantAd(samples, place, owner, new Date(), slug), tourIntroListing: sampleIntroListing(samples, place) };
};
